import { featureCollection, intersect as turfIntersect } from '@turf/turf';
import type {
  Feature as GeoJSONFeature,
  Polygon as GeoJSONPolygon,
} from 'geojson';
import { Feature } from 'ol';
import { GeoJSON } from 'ol/format';
import { Geometry, Polygon } from 'ol/geom';
import VectorSource from 'ol/source/Vector';
import { v4 } from 'uuid';
import { getRoundedFeatureArea } from './getRoundedFeatureArea';

// Conosciuta anche come 102100 (ESRI) o 3857 (EPSG)
const geoJSON = new GeoJSON({ featureProjection: 'EPSG:3857' });

/**
 * Calcola l'intesezione tra le geometrie disegnate e quelle presenti nel layer
 */
export function computeFeaturesIntersection(
  featuresSource: VectorSource,
  intersectionSource: VectorSource,
  onFeatureFound: (
    intersectable: Feature<Geometry>,
    feature: Feature<Polygon>,
    area: number,
  ) => void,
) {
  // 3. Iteriamo sulle feature disegnate
  const features = featuresSource.getFeatures();
  const featuresExtent = featuresSource.getExtent();

  for (const feature of features) {
    if (feature.getGeometry()?.getType() !== 'Polygon') return;
    const featureJson: GeoJSONFeature<GeoJSONPolygon> =
      geoJSON.writeFeatureObject(feature as Feature<Polygon>) as any;

    // 3.1 Iteriamo sulle feature del layer di intersezione che intersecano
    // la geometria disegnata. Utilizziamo la funzione
    // `forEachFeatureIntersectingExtent` per evitare di caricare tutte le
    // geometrie del layer di intersezione.
    intersectionSource.forEachFeatureIntersectingExtent(
      featuresExtent,
      intersectable => {
        if (intersectable.getGeometry()?.getType() !== 'Polygon') return;
        const intersectableJson: GeoJSONFeature<GeoJSONPolygon> =
          geoJSON.writeFeatureObject(intersectable as Feature<Polygon>) as any;

        const intersections = turfIntersect(
          featureCollection([featureJson, intersectableJson]),
        );

        // Se non ci sono intersezioni, passiamo alla prossima feature
        if (!intersections) return;

        // Le intersezioni sono delle geometrie di tipo Polygon restituite
        // da Turf.js. Le convertiamo in feature OpenLayers e le aggiungiamo
        // alla lista delle intersezioni.
        const intersectionFeatures = geoJSON.readFeatures(intersections);
        for (const intersectionFeature of intersectionFeatures) {
          intersectionFeature.setId(v4());
          const area = getRoundedFeatureArea(intersectionFeature);

          onFeatureFound(
            intersectable,
            intersectionFeature as Feature<Polygon>,
            area,
          );
        }
      },
    );
  }
}
